import { createContext } from "react";
import { StreamerRef, TargetWindow } from "./types";

interface IStreamerContext {
  streamerRef: React.RefObject<StreamerRef>;
  /**
   * Whether the audio is muted or not
   */
  isAudioMuted: boolean;
  /**
   * Set the audio muted state
   */
  setIsAudioMuted: (muted: boolean) => void;
  /**
   * Toggle the audio output
   */
  toggleAudioOutput: () => void;
  /**
   * Whether the microphone is muted or not
   */
  isMicrophoneMuted: boolean;
  /**
   * Toggles the audio input (microphone)
   */
  toggleMicrophone: () => void;
  /**
   * Whether the streamer is shown fullscreen or not
   */
  isFullscreen: boolean;
  /**
   * Set the fullscreen state
   * @param fullscreen desired fullscreen state
   */
  setIsFullscreen: (fullscreen: boolean) => void;
  /**
   * Toggles the fullscreen state
   */
  toggleFullscreen: () => void;
  /**
   * The list of windows that can be streamed / switched to
   */
  windows: TargetWindow[];
  /**
   * The currently selected window handle
   */
  selectedWindowHandle?: TargetWindow["handle"];
  /**
   * Set the list of windows when received from the streamer
   * @param windows the list of windows
   */
  setWindows: (windows: TargetWindow[]) => void;
  /**
   * Called when the window should be changed
   * @param windowHandle the new window handle
   */
  onWindowChanged: (windowHandle: number) => void;
  /**
   * Whether the streamer is connected to the remote server
   */
  isConnected: boolean;
  /**
   * Set the connected state
   * @param connected the new connected state
   */
  setIsConnected: (connected: boolean) => void;
  /**
   * Sends the local clipboard content to the remote server and pastes it there
   */
  pasteFromClipboard: () => void;
  /**
   * Triggers the copy to clipboard action on the remote server and writes the content to the local clipboard
   */
  copyToClipboard: () => void;
  /**
   * Whether the audio output is blocked by the browser policy.
   */
  isAudioOutputBlockedByBrowserPolicy: boolean;
  /**
   * Set the audio output blocked by browser policy state
   */
  setIsAudioOutputBlockedByBrowserPolicy: (blocked: boolean) => void;
}

export const StreamerContext = createContext<IStreamerContext>(
  {} as IStreamerContext,
);
